import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Stack, useToast, Flex } from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { createNewDocType, getDocTypeData, updateDocTypeData } from '../../services/DocumentTypes';
import { reverseTransformData } from '../../util/helper';
import { handleApiError, showSuccessToast } from '../../util/errorHandler';

const Section1 = () => {
    const { alias } = useParams();
    const [processedData, setProcessedData] = useState({});
    const [quickSave, setQuickSave] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();

    const schema = {
        "title": "Document Types Fields",
        "description": "",
        "type": "object",
        "properties": {
            "name": {
                "type": "string",
                "title": "Name",
                "default": ""
            },
            "alias": {
                "type": "string",
                "title": "Alias",
                "default": ""
            },
            "classification": {
                "type": "string",
                "title": "Classification",
                "default": ""
            },
            "doc_type": {
                "type": "string",
                "title": "Document Type",
                "default": ""
            },
            "cdt_image": {
                "type": "string",
                "title": "Direct URL of Cover Image",
                "default": ""
            },
            "languages": {
                "type": "array",
                "title": "Languages",
                "description": "Translations for different languages",
                "items": {
                    "type": "object",
                    "properties": {
                        "lang_code": {
                            "type": "string",
                            "title": "Language Code",
                            "description": "Language code (en or hin)",
                            "enum": ["en", "hin"],
                            "default": "en"
                        },
                        "value": {
                            "type": "string",
                            "title": "Translation Value",
                            "description": "The translated text"
                        }
                    },
                    "required": ["lang_code", "value"]
                },
                "default": []
            },
            "navigation_url": {
                "type": "string",
                "title": "Navigation URL",
                "description": ""
            }
        },
        required: ['name', 'alias']
    };

    const uiSchema = {
        "ui:submitButtonOptions": {
            "submitText": "Save",
            "norender": false,
            "props": {
                "disabled": false,
                "className": "button button--primary button--blue",
                "loadingText": "Saving..",
                "colorScheme": "teal"
            }
        },
        name: {
            'ui:emptyValue': ''
        },
        alias: {
            'ui:emptyValue': '',
            'ui:readonly': !!alias
        },
        classification: {
            'ui:emptyValue': ''
        },
        doc_type: {
            'ui:emptyValue': ''
        },
        cdt_image: {
            'ui:emptyValue': ''
        },
        languages: {
            'ui:options': {
                orderable: true,
                addable: true,
                removable: true
            },
            items: {
                'ui:ObjectFieldTemplate': (props) => {
                    return (
                        <Flex gap={4} align="flex-start" width="100%">
                            {props.properties.map((element, index) => (
                                <Box
                                    key={index}
                                    flex={element?.name === 'lang_code' ? '0 0 200px' : '1'}
                                    minWidth={element?.name === 'lang_code' ? '200px' : '0'}
                                >
                                    {element.content}
                                </Box>
                            ))}
                        </Flex>
                    );
                },
                lang_code: {
                    'ui:options': {
                        enumOptions: [
                            { value: 'en', label: 'en' },
                            { value: 'hin', label: 'hin' }
                        ]
                    }
                },
                value: {
                    'ui:emptyValue': '',
                    'ui:widget': 'textarea',
                    'ui:options': {
                        rows: 1,
                        width: "100%"
                    }
                }
            }
        },
        navigation_url: {
            'ui:emptyValue': ''
        }
    };

    const formData = processedData;

    // Fetch initial data
    useEffect(() => {
        const getBasicData = async () => {
            try {
                if (alias) {
                    const result = await getDocTypeData(alias);
                    if (result && result?.data?.[0]) {
                        // To Show image on form need to provide url to image field which is only used to show the image
                        setProcessedData(result?.data?.[0]);
                    }
                }
            } catch (error) {
                handleApiError(error, toast, {
                    defaultErrorMessage: "Failed to fetch data"
                });
            }
        };

        getBasicData();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alias, quickSave]);

    // Submit updated data
    const handleSubmit = async (formData) => {

        const updatedFormData = reverseTransformData(formData, []);

        try {
            if (alias) {
                const result = await updateDocTypeData(alias, updatedFormData);

                if (result?.status === 200) {
                    setQuickSave(prev => !prev);
                    showSuccessToast(toast, "Data updated successfully");
                }
            } else {
                const result = await createNewDocType(updatedFormData);  // Call the create API
                if (result?.status === 201) {
                    setQuickSave(prev => !prev);
                    showSuccessToast(toast, "New document type created successfully");
                    navigate(`/document-types/${result?.data?.alias}`);  // Navigate to the newly created post
                }
            }
        } catch (error) {
            handleApiError(error, toast);
        }
    };

    return (
        <Stack flexDirection={'row'} gap={"15px"} alignItems={"flex-start"}>
            <Box width={"600px"}>
                <Form
                    schema={schema}
                    uiSchema={uiSchema}
                    formData={formData}
                    validator={validator}
                    onSubmit={(e) => handleSubmit(e.formData)}
                />
            </Box>
        </Stack>
    );
};

export default Section1;